/**
 * Farmer "Where to buy": Green Bay dealers within a real radius of the farmer, on a map, and the
 * Green Bay field team (Sales Person / Sales Head) that covers that area.
 *
 * The centre is always the farmer — their current location when they share it, else the map pin on
 * their account, else their state as a fence. The server decides which (GET /retailers/where-to-buy)
 * and says so; this page prints that basis, because "near your phone" and "near the farm address on
 * your account" are different claims. It never falls back to the national list.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
  Store, MapPin, LocateFixed, Phone, MessageCircle, Navigation, Users, Loader2, Headset, Home, AlertTriangle,
} from 'lucide-react';
import { request } from '../../api/client';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../../lib/mapTiles';
import { STATE_NAME } from '../../constants/indianStates';
import { Button, Card, Alert, PageHeader, Segmented, Avatar, cn } from '../../ui';

type Source = 'DEVICE' | 'PROFILE' | 'STATE' | 'NONE';
type Retailer = {
  id: string; code: string | null; companyName: string; contactPerson: string | null;
  mobile: string | null; whatsappNumber: string | null;
  addressLine1: string | null; addressLine2: string | null; city: string | null;
  stateCode: string | null; pincode: string | null;
  latitude: number | null; longitude: number | null; distanceMeters: number | null;
};
type Staff = {
  id: string; name: string; roleCode: 'SALES_PERSON' | 'SALES_HEAD';
  mobile: string | null; photoUrl: string | null;
  coverage: { level: string; name: string; code: string | null };
  isMyRep: boolean;
};
type WhereToBuy = {
  origin: { lat: number; lng: number } | null; source: Source; radiusKm: number;
  stateCode: string | null; retailers: Retailer[]; staff: Staff[];
};
type MyRep = { company: { name: string; phone: string | null } };

const RADII = [5, 10, 25, 50, 100];
const DEFAULT_RADIUS = 25;

const fmtKm = (m: number | null) => (m == null ? '' : m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`);
const hasPoint = (r: { latitude: number | null; longitude: number | null }) =>
  r.latitude != null && r.longitude != null && !(r.latitude === 0 && r.longitude === 0);
const tel = (n: string) => { window.location.href = `tel:${n}`; };
const wa = (n: string) => window.open(`https://wa.me/${n.replace(/[^0-9]/g, '')}`, '_blank', 'noopener');

/** "Sundargarh district", "Odisha (statewide)", or a local area name. */
function coverageText(c: Staff['coverage']): string {
  if (!c?.name) return '';
  if (c.level === 'state') return `${c.name} (statewide)`;
  if (c.level === 'district') return `${c.name} district`;
  if (c.level === 'zone') return `${c.name} zone`;
  return c.name;
}

// Self-contained pins (Leaflet's default marker images don't resolve under bundlers).
const pin = (color: string) => L.divIcon({
  className: '', iconSize: [28, 38], iconAnchor: [14, 38], popupAnchor: [0, -34],
  html: `<svg viewBox="0 0 24 32" width="28" height="38" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill="${color}" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="4.5" fill="white"/></svg>`,
});
const YOU_HEX = '#1677FF';
const DEALER_HEX = '#339022';
const ICON_YOU = pin(YOU_HEX);
const ICON_DEALER = pin(DEALER_HEX);

/** Frame the farmer's circle (and every dealer in it); re-fit whenever the answer changes. */
function Frame({ origin, radiusKm, points }: { origin: { lat: number; lng: number } | null; radiusKm: number; points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 150);
    if (origin) {
      const b = L.latLng(origin.lat, origin.lng).toBounds(radiusKm * 2000);
      points.forEach((p) => b.extend(p));
      map.fitBounds(b, { padding: [20, 20] });
    } else if (points.length) {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 12 });
    }
    return () => clearTimeout(t);
  }, [map, origin, radiusKm, points]);
  return null;
}

const SOURCE_TEXT: Record<Source, string> = {
  DEVICE: 'your current location',
  PROFILE: 'the farm location on your account',
  STATE: 'your state',
  NONE: '',
};

export function WhereToBuyPage() {
  const [params, setParams] = useSearchParams();
  const rParam = Number(params.get('r'));
  const radiusKm = RADII.includes(rParam) ? rParam : DEFAULT_RADIUS;
  const setRadius = (r: number) => {
    const next = new URLSearchParams(params);
    if (r === DEFAULT_RADIUS) next.delete('r'); else next.set('r', String(r));
    setParams(next, { replace: true });
  };

  // The device point is opt-in: without it the server centres on the account's own pin.
  const [device, setDevice] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState<string | null>(null);

  /** `fresh` = the farmer pressed the button: a cached fix may be from where they were an hour ago. */
  const locate = (fresh = true) => {
    if (!('geolocation' in navigator)) { setLocError('This browser cannot share a location.'); return; }
    setLocating(true); setLocError(null);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setLocating(false);
        setDevice({ lat: Math.round(p.coords.latitude * 1e6) / 1e6, lng: Math.round(p.coords.longitude * 1e6) / 1e6 });
      },
      (e) => {
        setLocating(false);
        setLocError(e.code === e.PERMISSION_DENIED
          ? 'Location permission was refused, so we are using the location on your account.'
          : 'Your location could not be read just now, so we are using the location on your account.');
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: fresh ? 0 : 60 * 1000 },
    );
  };

  // Already allowed on this browser → use it straight away; never pop a prompt uninvited.
  useEffect(() => {
    navigator.permissions?.query({ name: 'geolocation' as PermissionName })
      .then((s) => { if (s.state === 'granted') locate(false); })
      .catch(() => { /* permissions API unavailable — the button still works */ });
  }, []);

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['where-to-buy', device?.lat, device?.lng, radiusKm],
    queryFn: () => request<WhereToBuy>({
      url: '/retailers/where-to-buy',
      params: { radiusKm, ...(device ? { lat: device.lat, lng: device.lng } : {}) },
    }),
    enabled: !locating,
    placeholderData: (prev) => prev,
  });
  const { data: myRep } = useQuery({
    queryKey: ['profile-my-rep'],
    queryFn: () => request<MyRep>({ url: '/profile/my-rep' }),
  });

  const retailers = data?.retailers ?? [];
  const staff = data?.staff ?? [];
  const mapped = useMemo(() => retailers.filter(hasPoint), [retailers]);
  const points = useMemo(() => mapped.map((r) => [r.latitude as number, r.longitude as number] as [number, number]), [mapped]);
  const nextRadius = RADII.find((r) => r > radiusKm);
  const stateName = data?.stateCode ? (STATE_NAME.get(data.stateCode) ?? data.stateCode) : '';
  const company = myRep?.company;

  const basis = !data ? '' : data.source === 'STATE'
    ? `Dealers in ${stateName || 'your state'} — there is no map pin on your account, so distances can't be shown.`
    : data.source === 'NONE'
      ? ''
      : `Within ${data.radiusKm} km of ${SOURCE_TEXT[data.source]}.`;

  return (
    <div className="p-1">
      <PageHeader
        icon={<Store />}
        title="Where to buy"
        subtitle="Green Bay dealers near you, and the Green Bay team that covers your area."
        actions={(
          <Button
            icon={locating ? <Loader2 className="size-4 animate-spin" /> : <LocateFixed className="size-4" />}
            onClick={() => locate()}
            disabled={locating}
            variant={data?.source === 'DEVICE' ? 'default' : 'primary'}
          >
            {data?.source === 'DEVICE' ? 'Refresh my location' : 'Use my current location'}
          </Button>
        )}
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 overflow-x-auto">
          <Segmented
            value={radiusKm}
            onChange={(v) => setRadius(Number(v))}
            disabled={data?.source === 'STATE' || data?.source === 'NONE'}
            options={RADII.map((r) => ({ label: `${r} km`, value: r }))}
          />
        </div>
        {basis && (
          <p className="flex min-w-0 items-start gap-1.5 text-sm text-muted">
            {data?.source === 'DEVICE' ? <LocateFixed className="mt-0.5 size-4 shrink-0 text-brand-600" /> : <Home className="mt-0.5 size-4 shrink-0 text-brand-600" />}
            <span>{basis}</span>
            {device && data?.source === 'DEVICE' && (
              <button type="button" className="ml-1 shrink-0 text-brand-600 hover:underline" onClick={() => setDevice(null)}>Use my account location</button>
            )}
          </p>
        )}
      </div>

      {locError && <Alert type="warning" className="mb-4" message={locError} />}

      {isError ? (
        <Card>
          <div className="py-8 text-center">
            <AlertTriangle className="mx-auto size-7 text-danger-500" />
            <p className="mt-2 type-body-medium text-content">Dealers near you could not be loaded.</p>
            <p className="mt-0.5 text-sm text-muted">This is a connection problem, not an empty area.</p>
            <Button className="mt-3" onClick={() => refetch()}>Try again</Button>
          </div>
        </Card>
      ) : isLoading || !data ? (
        <Card loading><div className="h-64" /></Card>
      ) : data.source === 'NONE' ? (
        <Card>
          <div className="py-8 text-center">
            <MapPin className="mx-auto size-7 text-subtle" />
            <p className="mt-2 type-body-medium text-content">We don't know where you are yet.</p>
            <p className="mx-auto mt-0.5 max-w-md text-sm text-muted">
              Your account has no farm location or state. Share your current location to see the dealers around you,
              or ask your representative to add your farm location.
            </p>
            <Button className="mt-3" variant="primary" icon={<LocateFixed className="size-4" />} onClick={() => locate()}>Use my current location</Button>
          </div>
        </Card>
      ) : (
        <div className={cn('grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)]', isFetching && 'opacity-70 transition-opacity')}>
          <div className="flex min-w-0 flex-col gap-4">
            {/* Map: the farmer, their search circle, and every dealer inside it that has a pin. */}
            <div className="overflow-hidden rounded-card border border-line bg-surface">
              <div style={{ height: 380 }}>
                <MapContainer center={data.origin ? [data.origin.lat, data.origin.lng] : (points[0] ?? [22.5, 80])} zoom={10} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                  <TileLayer attribution={MAP_TILE_ATTRIBUTION} url={MAP_TILE_URL} />
                  <Frame origin={data.origin} radiusKm={data.radiusKm} points={points} />
                  {data.origin && (
                    <>
                      <Circle center={[data.origin.lat, data.origin.lng]} radius={data.radiusKm * 1000}
                        pathOptions={{ color: YOU_HEX, weight: 1.5, fillOpacity: 0.06 }} />
                      <Marker position={[data.origin.lat, data.origin.lng]} icon={ICON_YOU}>
                        <Popup>{data.source === 'DEVICE' ? 'You are here' : 'Your farm (from your account)'}</Popup>
                      </Marker>
                    </>
                  )}
                  {mapped.map((r, i) => (
                    <Marker key={r.id} position={[r.latitude as number, r.longitude as number]} icon={ICON_DEALER}>
                      <Popup>
                        <b>{i + 1}. {r.companyName}</b>{r.distanceMeters != null ? ` · ${fmtKm(r.distanceMeters)}` : ''}
                        <br />{[r.addressLine1, r.city].filter(Boolean).join(', ')}
                        {r.mobile ? <><br />📞 {r.mobile}</> : null}
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-line px-3 py-2 text-xs text-muted">
                {data.origin && <span className="inline-flex items-center gap-1"><span className="size-2.5 rounded-full" style={{ background: YOU_HEX }} /> {data.source === 'DEVICE' ? 'You' : 'Your farm'}</span>}
                <span className="inline-flex items-center gap-1"><span className="size-2.5 rounded-full" style={{ background: DEALER_HEX }} /> Dealer ({mapped.length} on map)</span>
                {retailers.length > mapped.length && <span>{retailers.length - mapped.length} without a map pin — listed below</span>}
              </div>
            </div>

            {/* Dealers */}
            <Card title={<span className="inline-flex items-center gap-2"><Store className="size-4 text-brand-600" /> Dealers ({retailers.length})</span>}>
              {retailers.length === 0 ? (
                <div className="py-6 text-center">
                  <Store className="mx-auto size-7 text-subtle" />
                  <p className="mt-2 type-body-medium text-content">
                    {data.source === 'STATE' ? `No Green Bay dealer in ${stateName || 'your state'} yet.` : `No Green Bay dealer within ${data.radiusKm} km.`}
                  </p>
                  {nextRadius && data.source !== 'STATE' ? (
                    <Button className="mt-3" onClick={() => setRadius(nextRadius)}>Search within {nextRadius} km</Button>
                  ) : (
                    <p className="mt-0.5 text-sm text-muted">The Green Bay team below can tell you where to get our products.</p>
                  )}
                </div>
              ) : (
                <ol aria-label="Dealers near you" className="flex flex-col gap-3">
                  {retailers.map((r, i) => {
                    const address = [r.addressLine1, r.addressLine2, r.city, r.pincode].filter(Boolean).join(', ');
                    const waNum = r.whatsappNumber || r.mobile;
                    const dir = hasPoint(r)
                      ? `https://www.google.com/maps/dir/?api=1&destination=${r.latitude},${r.longitude}`
                      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([r.companyName, r.city].filter(Boolean).join(', '))}`;
                    return (
                      <li key={r.id} className="rounded-control border border-line p-3">
                        <div className="flex items-start gap-3">
                          <span className="grid size-7 shrink-0 place-items-center rounded-full bg-brand-50 type-caption-strong text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">{i + 1}</span>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                              <span className="min-w-0 font-semibold text-content [overflow-wrap:anywhere]">{r.companyName}</span>
                              {r.distanceMeters != null && <span className="shrink-0 type-label-strong text-brand-700 dark:text-brand-300">{fmtKm(r.distanceMeters)}</span>}
                            </div>
                            {r.contactPerson && <div className="text-xs text-muted">{r.contactPerson}</div>}
                            {address && (
                              <div className="mt-1 flex items-start gap-1.5 text-sm text-content">
                                <MapPin className="mt-0.5 size-3.5 shrink-0 text-muted" /> <span className="min-w-0 [overflow-wrap:anywhere]">{address}</span>
                              </div>
                            )}
                            <div className="mt-2.5 grid grid-cols-3 gap-2">
                              <Button size="sm" icon={<Phone className="size-4" />} disabled={!r.mobile} onClick={() => r.mobile && tel(r.mobile)}>Call</Button>
                              <Button size="sm" icon={<MessageCircle className="size-4" />} disabled={!waNum} onClick={() => waNum && wa(waNum)}>WhatsApp</Button>
                              <Button size="sm" icon={<Navigation className="size-4" />} onClick={() => window.open(dir, '_blank', 'noopener')}>Directions</Button>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </Card>
          </div>

          {/* The team covering this area */}
          <Card
            className="h-fit"
            title={<span className="inline-flex items-center gap-2"><Users className="size-4 text-brand-600" /> Green Bay team for this area</span>}
          >
            {staff.length === 0 ? (
              <div className="py-4 text-center">
                <Headset className="mx-auto size-7 text-subtle" />
                <p className="mt-2 type-body-medium text-content">No Green Bay representative covers this area yet.</p>
                {company?.phone ? (
                  <>
                    <p className="mt-0.5 text-sm text-muted">Reach {company.name} for help finding our products.</p>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <Button size="sm" icon={<Phone className="size-4" />} onClick={() => tel(company.phone!)}>Call</Button>
                      <Button size="sm" variant="primary" icon={<MessageCircle className="size-4" />} onClick={() => wa(company.phone!)}>WhatsApp</Button>
                    </div>
                  </>
                ) : (
                  <p className="mt-0.5 text-sm text-muted">Please contact the company for help.</p>
                )}
              </div>
            ) : (
              <ul aria-label="Green Bay team for this area" className="flex flex-col gap-3">
                {staff.map((s) => (
                  <li key={s.id} className="rounded-control border border-line p-3">
                    <div className="flex items-center gap-3">
                      <Avatar src={s.photoUrl ?? undefined} size={40} fallback={<Users className="size-5" />} className="bg-brand-600 text-white" />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-content [overflow-wrap:anywhere]">{s.name}</div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                          <span className={cn(
                            'inline-flex items-center rounded-full px-2 py-0.5 type-caption-strong',
                            s.roleCode === 'SALES_HEAD'
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                              : 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
                          )}>
                            {s.roleCode === 'SALES_HEAD' ? 'Sales Head' : 'Sales Representative'}
                          </span>
                          {s.isMyRep && <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 type-caption-strong text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">Your representative</span>}
                        </div>
                      </div>
                    </div>
                    {coverageText(s.coverage) && (
                      <div className="mt-2 flex items-start gap-1.5 text-sm text-content">
                        <MapPin className="mt-0.5 size-3.5 shrink-0 text-brand-600" /><span><span className="text-muted">Covers </span>{coverageText(s.coverage)}</span>
                      </div>
                    )}
                    {s.mobile && (
                      <div className="mt-2.5 grid grid-cols-2 gap-2">
                        <Button size="sm" icon={<Phone className="size-4" />} onClick={() => tel(s.mobile!)}>Call</Button>
                        <Button size="sm" variant="primary" icon={<MessageCircle className="size-4" />} onClick={() => wa(s.mobile!)}>WhatsApp</Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
